// src/services/auth.ts
import { http } from "./http";
import { tokenStore } from "./token";

/* ======================
   TYPES (theo AuthController)
====================== */

export type LoginUser = {
  username: string;
  email: string;
  role: string | null;
};

export type LoginResponse = {
  token: string;
  user: LoginUser;
};
export type GoogleLoginResponse = LoginResponse;

export type RegisterResponse = {
  message: string;
};

export type ApiMessage = {
  message?: string;
  Message?: string;
};

// helper: set Authorization default header
const setAuthHeader = (token?: string | null) => {
  if (token) http.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete (http.defaults.headers.common as any).Authorization;
};

function pickMessage(err: any, fallback: string) {
  const d = err?.response?.data as ApiMessage | undefined;
  return d?.message || d?.Message || fallback;
}

export const authApi = {
  /* ======================
     LOGIN
     POST /api/Auth/login
     body: { usernameOrEmail, password, rememberMe }
     response: { token, user }
  ====================== */
  async login(usernameOrEmail: string, password: string, rememberMe = true) {
    const res = await http.post<LoginResponse>("/api/Auth/login", {
      usernameOrEmail,
      password,
      rememberMe,
    });

    const { token } = res.data || ({} as any);
    if (!token) throw new Error("Invalid auth response (missing token)");

    // tokenStore của bạn đang set(access, refresh) => backend chỉ có 1 token
    tokenStore.set(token, "");
    setAuthHeader(token);

    return res.data;
  },

    /* ======================
     GOOGLE LOGIN
     POST /api/Auth/google-login
     body: { token, rememberMe }
     response: { token, user }
  ====================== */
  async googleLogin(firebaseIdToken: string, rememberMe = true) {
    const res = await http.post<LoginResponse>("/api/Auth/google-login", {
      token: firebaseIdToken,
      rememberMe,
    });

    const { token } = res.data || ({} as any);
    if (!token) throw new Error("Invalid auth response (missing token)");

    // lưu token giống login thường
    tokenStore.set(token, "");
    setAuthHeader(token);

    return res.data;
  },

  /* ======================
     REGISTER
     POST /api/Auth/register
     body: { username, email, password, roleId }
     response: { message }
  ====================== */
  async register(data: {
    username: string;
    email: string;
    password: string;
    roleId: number;
  }) {
    const res = await http.post<RegisterResponse>("/api/Auth/register", data);
    return res.data;
  },

  /* ======================
     CONFIRM EMAIL (OTP)
     POST /api/Auth/confirm-email
     body: { otp }
     response: { Message }
  ====================== */
  async confirmEmail(otp: string) {
    const res = await http.post("/api/Auth/confirm-email", { otp });
    return res.data;
  },

  /* ======================
     FORGOT PASSWORD
     POST /api/Auth/forgot-password
     body: { email }
  ====================== */
  async forgotPassword(email: string) {
    const res = await http.post("/api/Auth/forgot-password", { email });
    return res.data;
  },

  /* ======================
     VERIFY OTP
     POST /api/Auth/verify-otp
     body: { otp }
     response: { UserId }
  ====================== */
  async verifyOtp(otp: string) {
    const res = await http.post<{ UserId: number }>("/api/Auth/verify-otp", {
      otp,
    });
    return res.data;
  },

  /* ======================
     RESET PASSWORD BY OTP
     POST /api/Auth/reset-password-otp
     body: { userId, newPassword }
  ====================== */
  async resetPasswordOtp(userId: number, newPassword: string) {
    const res = await http.post("/api/Auth/reset-password-otp", {
      userId,
      newPassword,
    });
    return res.data;
  },

  /* ======================
     CHANGE PASSWORD (Authorized)
     POST /api/Auth/change-password
     body: { userId, currentPassword, newPassword }
  ====================== */
  async changePassword(data: {
    userId: number;
    currentPassword: string;
    newPassword: string;
  }) {
    const res = await http.post("/api/Auth/change-password", data);
    return res.data;
  },

  /* ======================
     INIT AUTH HEADER FROM STORED TOKEN
     gọi trong App init (optional)
  ====================== */
  initFromStorage() {
    const token = tokenStore.getAccess();
    if (token) setAuthHeader(token);
  },

  /* ======================
     LOGOUT
  ====================== */
  logout() {
    tokenStore.clear();
    setAuthHeader(null);
  },

  /* ======================
     ERROR MESSAGE HELPER (optional)
  ====================== */
  getErrorMessage(err: any) {
    return pickMessage(err, "Có lỗi xảy ra. Vui lòng thử lại.");
  },
};
